/**
 * Device Unlock API Configuration
 * Centralized configuration for all device unlock related APIs
 * Follows coding standards for secret and configuration management
 * 
 * SECURITY NOTICE:
 * - All API keys and sensitive configuration MUST be provided via environment variables
 * - NO hardcoded fallback values are allowed for security credentials
 * - Application will fail fast if required environment variables are missing
 */

import { log } from '../utils/logger';

/**
 * SOE (Service Orchestration Engine) API Configuration
 * SECURITY: All sensitive values must be provided via environment variables
 */
export const getURLBase = (domainName?: string) => {
  if (!domainName) {
    return process.env.NEXT_PUBLIC_API_BASE_URL_NEW_PROD;
  }
  if (domainName.includes('sit1')) {
    return process.env.NEXT_PUBLIC_API_BASE_URL_NEW_SIT1;
  } else if (domainName.includes('sit2')) {
    return process.env.NEXT_PUBLIC_API_BASE_URL_NEW_TEST;
  } else if (domainName.includes('prod2e')) {
    return process.env.NEXT_PUBLIC_API_BASE_URL_NEW_PROD2E;
  } else if (
    domainName.includes('sita') ||
    domainName.includes('dit1') ||
    domainName.includes('localhost')
  ) {
    return process.env.NEXT_PUBLIC_API_BASE_URL_NEW_DIT1;
  }
  return process.env.NEXT_PUBLIC_API_BASE_URL_NEW_PROD;
};

export const SOE_CONFIG = {  
  // Base URL for SOE API
  get BASE_URL() {
    return process.env.NEXT_PUBLIC_SOE_API_BASE_URL || 'https://jzpv-soe-digital-nsdit1.ebiz.verizon.com';
  },
  
  // API Endpoints
  ENDPOINTS: {
      ELIGIBILITY: '/soe/api/device-unlock/eligibility',
      CASE_CREATION: '/soe/api/device-unlock/unlock',
  },
  
  // Request Headers - Standard across all SOE APIs
  HEADERS: {
    get CHANNEL_ID() {
      return process.env.NEXT_PUBLIC_CHANNEL_ID || 'DOTCOM';
    },
    get API_KEY() {
      const apiKey = process.env.NEXT_PUBLIC_SOE_API_KEY;
      
      // Allow tests to run without API key ONLY in test environment (not production, even in Jest)
      if (!apiKey && process.env.NODE_ENV === 'test') {
        return 'test-api-key';
      }
      
      if (!apiKey) {
        log.error('🚨 CRITICAL: NEXT_PUBLIC_SOE_API_KEY environment variable is not set');
        throw new Error('NEXT_PUBLIC_SOE_API_KEY environment variable is required for SOE API access');
      }
      return apiKey;
    },
    CONTENT_TYPE: 'application/json',
  },
};

/**
 * Device Unlock Service Configuration
 */
export const DEVICE_UNLOCK_CONFIG = {
  // Query parameters for eligibility check
  ELIGIBILITY_QUERY_PARAMS: {
    type: 'HANDSET',
    sourceSystem: 'DOTCOM',
  },
  
  // Brand configuration
  BRANDS: {
    TRACFONE: 'tracfone',
    STRAIGHTTALK: 'straighttalk',
    SAFELINK: 'safelink',
    SIMPLEMOBILE: 'simplemobile',
    TOTALWIRELESS: 'totalwireless',
    NET10WIRELESS: 'net10wireless',
    FAMILYMOBILE: 'familymobile',
    CLEARWAY: 'clearway',
  },
  
  // Default brand for testing
  DEFAULT_BRAND: 'tracfone',
};

/**
 * Get fully constructed URL for SOE API endpoint
 * @param endpoint - The endpoint key (e.g., 'ELIGIBILITY', 'CASE_CREATION')
 * @returns Full URL for the endpoint
 */
export const getSoeUrl = (endpoint: keyof typeof SOE_CONFIG.ENDPOINTS, domainName?: string): string => {
  return `${getURLBase(domainName)}${SOE_CONFIG.ENDPOINTS[endpoint]}`;
};

/**
 * Get standard headers for SOE API requests
 * @param additionalHeaders - Optional additional headers to merge
 * @returns Headers object for API requests
 */
export const getSoeHeaders = (additionalHeaders?: Record<string, string>): Record<string, string> => {
  return {
    'Content-Type': SOE_CONFIG.HEADERS.CONTENT_TYPE,
    'channelid': SOE_CONFIG.HEADERS.CHANNEL_ID,
    'x-soe-apikey': SOE_CONFIG.HEADERS.API_KEY,
    ...additionalHeaders,
  };
};

/**
 * Get JWT Token for authenticated requests
 * Fetches from NEXT_PUBLIC_JWT_TOKEN environment variable
 * In production, this should be dynamically generated from a backend endpoint
 * @returns JWT token string
 */
export const getJwtToken = (): string => {
  // Fetch from environment variable - should be set in .env.development or .env.production
  const token = process.env.NEXT_PUBLIC_JWT_TOKEN;
  
  if (!token) {
    log.warn('⚠️ NEXT_PUBLIC_JWT_TOKEN is not set in environment variables');
    throw new Error('NEXT_PUBLIC_JWT_TOKEN environment variable is required');
  }
  
  return token;
};
